import {
  ContractError,
  parseWorldNotificationUpdate,
  type WorldNotificationUpdatePayload,
} from './contracts';

export type WorldHistoryArea = Readonly<{
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
}>;
export type WorldHistoryRequest = Readonly<{
  area: WorldHistoryArea;
  since: number;
  baseRevision?: number;
  revertVersion?: number;
  requestId?: string;
}>;
export type WorldHistoryRow = Readonly<{
  version: number;
  actor: string;
  playerId: number;
  operation: string;
  at: number;
  objects: number;
  details: readonly string[];
  reason: string;
  canUndo: boolean;
}>;
export type WorldHistoryInspection = Readonly<{
  revision: number;
  since: number;
  earliest: number;
  rows: readonly WorldHistoryRow[];
  truncated: boolean;
  changes: number;
  skipped: number;
  reasons: readonly string[];
  canRollback: boolean;
}>;
export type WorldHistorySubmission = Readonly<{
  operation: string;
  version: number;
  changed: number;
  skipped: number;
  replayed: boolean;
  worldUpdate: WorldNotificationUpdatePayload;
}>;
export type WorldHistoryGateway = Readonly<{
  inspect: (request: WorldHistoryRequest, signal: AbortSignal) => Promise<WorldHistoryInspection>;
  rollback: (request: WorldHistoryRequest, signal: AbortSignal) => Promise<WorldHistorySubmission>;
}>;
function object(value: unknown): Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value))
    throw new ContractError('Invalid world history response');
  return value as Record<string, unknown>;
}
function number(value: unknown): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0)
    throw new ContractError('Invalid world history number');
  return value;
}
function text(value: unknown): string {
  if (typeof value !== 'string') throw new ContractError('Invalid world history text');
  return value;
}
function bool(value: unknown): boolean {
  if (typeof value !== 'boolean') throw new ContractError('Invalid world history flag');
  return value;
}
function envelope(value: unknown): Record<string, unknown> {
  const root = object(value);
  if (root.protocolVersion !== '2') throw new ContractError('Unsupported history protocol');
  return root;
}
export function parseWorldHistory(value: unknown): WorldHistoryInspection {
  const body = object(envelope(value).worldHistory);
  if (!Array.isArray(body.rows) || body.rows.length > 100 || !Array.isArray(body.reasons))
    throw new ContractError('Invalid world history rows');
  return {
    revision: number(body.revision),
    since: number(body.since),
    earliest: number(body.earliest),
    truncated: bool(body.truncated),
    changes: number(body.changes),
    skipped: number(body.skipped),
    canRollback: bool(body.canRollback),
    reasons: body.reasons.map(text),
    rows: body.rows.map((value) => {
      const row = object(value);
      if (!Array.isArray(row.details) || row.details.length > 9)
        throw new ContractError('Invalid history details');
      return {
        details: row.details.map(text),
        version: number(row.version),
        actor: text(row.actor),
        playerId: number(row.playerId),
        operation: text(row.operation),
        at: number(row.at),
        objects: number(row.objects),
        reason: text(row.reason),
        canUndo: bool(row.canUndo),
      };
    }),
  };
}
export function parseWorldHistorySubmission(value: unknown): WorldHistorySubmission {
  const root = envelope(value);
  const body = object(root.worldHistorySubmission);
  return {
    operation: text(body.operation),
    version: number(body.version),
    changed: number(body.changed),
    skipped: number(body.skipped),
    replayed: bool(body.replayed),
    worldUpdate: parseWorldNotificationUpdate(root.worldUpdate),
  };
}
